<script setup lang="ts">
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';

import { AppMasonryGallery, AppStars } from '@libs/ui';

import type { PeekPriceModalHotelOnlyItinerary } from '../../models';

import PeekPriceHotelOnlyRooms from './PeekPriceHotelOnlyRooms.vue';

interface PeekPriceHotelOnlyCardProps {
  itinerary: PeekPriceModalHotelOnlyItinerary;
}

const props = defineProps<PeekPriceHotelOnlyCardProps>();
</script>

<template>
  <div class="peek-price-hotel-only-card">
    <AppMasonryGallery
      :image-urls="props.itinerary.imageUrls"
      :alt-image-text="$t('a11y.image')"
      :allow-show-full-gallery-action="false"
    />
    <div class="stay-details">
      <div class="time-section">
        <div class="date-text">{{ props.itinerary.dates }}</div>
        <div class="nights-text">{{ props.itinerary.nights }}</div>
      </div>
      <div class="hotel-name-text">{{ props.itinerary.hotelName }}</div>
      <div class="hotel-destination-text">
        {{ props.itinerary.hotelDestination }}
      </div>

      <div class="rating-section">
        <AppStars :amount="props.itinerary.hotelStars" size="18" />
        <CustomersRatingTag
          v-if="props.itinerary.guestRating"
          :guest-rating="props.itinerary.guestRating"
        />
      </div>

      <div class="rooms-section">
        <PeekPriceHotelOnlyRooms
          v-if="props.itinerary.rooms"
          :rooms="props.itinerary.rooms"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.peek-price-hotel-only-card {
  height: 100%;
  display: grid;
  grid-template-rows: 55% 45%;

  .stay-details {
    .time-section {
      display: flex;
      margin: var(--spacing-12) 0;

      .date-text {
        @include font-label-m-light;
        color: var(--color-neutral-70);
        padding-right: var(--spacing-8);
        border-right: 1px solid var(--color-neutral-20);
        margin-right: var(--spacing-8);
      }

      .nights-text {
        @include font-label-m-light;
        color: var(--color-neutral-70);
      }
    }

    .hotel-name-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
      margin-bottom: var(--spacing-4);
    }

    .hotel-destination-text {
      @include font-label-s-light;
      color: var(--color-neutral-70);
      margin-bottom: var(--spacing-8);
    }

    .rating-section {
      display: flex;
      align-items: center;
      gap: var(--spacing-12);
    }

    .rooms-section {
      margin-top: var(--spacing-16);
    }
  }
}
</style>
